<template>
    <div>
        <div class="mb-3 text-right">
            <hts-form-button
                v-if="checkPermission(layoutPermissions.route.add)"
                color="main"
                icon-name="hi-plus-circle-icon"
                icon-position="right"
                type="submit"
                @click="openDrawer('create')">
                {{ $t('labels.add') }}
            </hts-form-button>
        </div>

        <hts-data-table
            ref="dataTables"
            :headers="headers"
            :server="apiGetRoutes"
            use-selection
            :use-archive="useArchive"
            store-name="routeDataTable"
            store-key="Route" />

        <AddRoute
            ref="addRoute"
            @refreshTables="refreshTables" />
        <EditRoute
            ref="editRoute"
            @refreshTables="refreshTables" />
        <DetailRoute ref="detailRoute" />
        <DeleteRoute
            ref="deleteRoute"
            @refreshTables="refreshTables" />
    </div>
</template>

<script setup lang="ts">
import AddRoute from './ignore/add.vue'
import DeleteRoute from './ignore/delete.vue'
import DetailRoute from './ignore/detail.vue'
import EditRoute from './ignore/edit.vue'
import Distance from './ignore/table/compoment/distance.vue'
import Duration from './ignore/table/compoment/duration.vue'
import Route from './ignore/table/compoment/route.vue'
import TollStation from './ignore/table/compoment/toll-station.vue'
import Actions from '~/pages/trucker-[id]/driver/ignore/actions.vue'

import layoutPermissions from '@/data/permissions/layout-permissions'
const { $formatters } = useNuxtApp()
const route = useRoute()

const addRoute = ref()
const editRoute = ref()
const detailRoute = ref()
const deleteRoute = ref()
const dataTables = ref()

// table
const apiGetRoutes = $url.route.route.gets

const useArchive = { api: $url.route.route.archive, permissions: checkPermission(layoutPermissions.route.archive) }

const store = $store()

const vehicleCategories: { [key: string]: any } = computed(() => {
  return store.data.getVehicleCategories
})

const filterItems = computed(() => {
  if (!vehicleCategories.value || !vehicleCategories.value.length) {
    return []
  }

  const results = vehicleCategories.value.map((item: any) => {
    return {
      name: item.name,
      value: item._id
      // class: { '!bg-amber-100': true, '!text-amber-800': true } // Màu cam đậm
    }
  })

  results.unshift({ name: $t('labels.all'), value: null })

  return results
})

onMounted(() => {
  const action = route.query.action
  if (action === 'create') {
    if (addRoute.value) {
      addRoute.value.open()
    }
  }
})

const openDrawer = (type: string, option?: { [key: string]: any }) => {
  switch (type) {
  case 'create':
    // handle open add
    if (addRoute.value) {
      addRoute.value.open()
    }
    break
  case 'edit':
    // handle open edit
    if (editRoute.value) {
      editRoute.value.open(option?.id)
    }
    break
  case 'detail':
    // handle open edit
    if (detailRoute.value) {
      detailRoute.value.open(option?.id)
    }
    break
  case 'delete':
    // handle open add
    if (deleteRoute.value) {
      deleteRoute.value.open(option?.id)
    }
    break

  default:
    break
  }
}

const refreshTables = () => {
  dataTables.value && dataTables.value.refresh()
}

const headers = ref<any[]>([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter',
    valueGetter: (params: any) => {
      return params.data.code || ''
    }
  },
  {
    field: 'positions',
    headerName: $t('labels.route'),
    minWidth: 400,
    filter: 'agTextColumnFilter',
    cellRenderer: Route
  },
  {
    field: 'vehicleCategoryIds',
    headerName: $t('labels.vehicleType'),
    valueGetter: (params: any) => {
      const { vehicleCategories } = params.data
      const vehicleCategoryNames = (vehicleCategories || []).map((item: any) => item.name)
      return vehicleCategoryNames.join(', ')
    },
    filter: 'TableFilterStatus',
    filterItems: filterItems.value,
    filterModelDefault: { type: 'equals', filterType: 'text' }
  },
  {
    headerName: $t('labels.tollStation'),
    minWidth: 400,
    // filter: 'agTextColumnFilter',
    cellRenderer: TollStation
  },
  {
    headerName: $t('labels.distance'),
    // filter: 'agTextColumnFilter',
    cellRenderer: Distance
  },
  {
    headerName: $t('labels.tolerance'),
    filter: 'agNumberColumnFilter',
    cellRendererParams: {
      keyDistance: 'tolerance'
    },
    cellRenderer: Distance
  },
  {
    headerName: $t('labels.totalKm'),
    cellRendererParams: {
      keyDistance: 'totalKm'
    },
    cellRenderer: Distance
  },
  {
    headerName: 'Thời gian',
    cellRenderer: Duration
  },
  {
    field: 'description',
    headerName: $t('labels.description')
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    cellRenderer: Actions,
    cellRendererParams: {
      storeKey: 'routeDataTable',
      storeName: 'Route',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.viewDetail'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.route.management),
          isAll: true,
          click: (data: any) => openDrawer('detail', { id: data._id })
        },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-main-600 hover:text-main-500',
          permissions: checkPermission(layoutPermissions.route.edit),
          click: (data: any) => openDrawer('edit', { id: data._id })
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.route.delete),
          isArchive: true,
          click: (data: any) => openDrawer('delete', { id: data._id })
        }
      ]
    }
  }
])
</script>
